<template>
  <v-card class="mb-8">
    <inherit-header
      title="出勤時刻の自動決定"
      :grouped="grouped"
      :customized="customized || isCustomizing"
      :disabled="saving"
      @customize="isCustomizing = true"
      @inherit="inherit"
    />

    <v-card-text>
      <p class="text-sm mb-1">
        キャストアプリや勤怠画面で打刻した時刻を、ここで決めた計算で「出勤時刻」に直します。
        店舗のスタッフが出勤時刻を手で直さなくて済むようにするための設定です。
      </p>
      <p class="text-xs secondary--text mb-2">
        例: 15分切り捨て・30分足す・20:30から、のとき 20:10 の打刻 → 20:30。
        オンにすると、下の「出勤時の申告」で項目ごとに出勤時刻への影響(遅らせる・専用の計算)を決められます。
        退勤と休憩の時刻は今までどおり「店舗設定 > タイムカード打刻設定」の丸めを使います。
      </p>

      <v-switch
        v-model="draft.enabled"
        label="打刻した時刻から出勤時刻を自動で決める"
        :disabled="!editable"
        hide-details
        inset
      />

      <template v-if="draft.enabled">
        <v-row class="mt-2">
          <v-col
            cols="6"
            md="3"
          >
            <v-select
              v-model="draft.roundingUnitMinutes"
              :items="roundingUnits"
              item-text="text"
              item-value="value"
              label="丸め単位"
              :disabled="!editable"
              hide-details
            />
          </v-col>

          <v-col
            cols="6"
            md="3"
          >
            <v-select
              v-model="draft.roundingMethod"
              :items="roundingMethods"
              item-text="text"
              item-value="value"
              label="丸め方法"
              :disabled="!editable"
              hide-details
            />
          </v-col>

          <v-col
            cols="6"
            md="3"
          >
            <v-text-field
              v-model="draft.addMinutes"
              label="丸めた後に足す時間"
              type="number"
              min="0"
              step="5"
              suffix="分"
              :disabled="!editable"
              hide-details
            />
          </v-col>

          <v-col
            cols="6"
            md="3"
          >
            <v-text-field
              v-model="draft.earliestTime"
              label="出勤を付け始める時刻"
              type="time"
              clearable
              :disabled="!editable"
              hide-details
            />
          </v-col>
        </v-row>

        <p class="text-xs mt-3">
          早く打刻しても、出勤時刻はこの時刻より前にはなりません。空にすると制限しません。
        </p>

        <template v-if="preview">
          <p class="text-sm mb-2">
            <span class="font-weight-semibold">計算例</span> この設定だと、出勤時刻はこうなります。
          </p>

          <v-simple-table dense>
            <thead>
              <tr>
                <th>打刻した時刻</th>
                <th
                  v-for="column in preview.columns"
                  :key="`check-in-preview-head-${column.key}`"
                >
                  {{ column.name }}
                </th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="row in preview.rows"
                :key="`check-in-preview-row-${row.punchedAt}`"
              >
                <td class="font-weight-semibold">
                  {{ row.punchedAt }}
                </td>
                <td
                  v-for="column in preview.columns"
                  :key="`check-in-preview-${row.punchedAt}-${column.key}`"
                  :class="{ 'check-in-preview--changed': row.results[column.key] !== row.results.none }"
                >
                  {{ row.results[column.key] }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </template>
      </template>

      <div
        v-if="editable"
        class="mt-6"
      >
        <v-btn
          color="primary"
          :ripple="false"
          :disabled="saving"
          @click="$emit('save', normalized)"
        >
          {{ isCustomizing && !customized ? 'この店舗だけの設定として保存' : '保存' }}
        </v-btn>

        <v-btn
          v-if="isCustomizing && !customized"
          text
          color="secondary"
          :ripple="false"
          @click="cancelCustomizing"
        >
          キャンセル
        </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  ref,
  computed,
  watch,
} from '@vue/composition-api'
import { debounce } from 'lodash'
import CheckInSettingsApi from '@/api/admin/CheckInSettings'
import InheritHeader from './InheritHeader.vue'
import { ROUNDING_METHODS } from './DeclarationItems.vue'

const DEFAULT_RULE = {
  enabled: false,
  roundingUnitMinutes: 15,
  roundingMethod: 'floor',
  addMinutes: 0,
  earliestTime: null,
}

export default {
  components: {
    InheritHeader,
  },
  props: {
    // 当てはまっている出勤時刻の計算(無ければ null)
    rule: {
      type: Object,
      default: null,
    },
    items: {
      type: Array,
      default: () => [],
    },
    grouped: {
      type: Boolean,
      default: false,
    },
    customized: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const draft = ref({ ...DEFAULT_RULE })
    const preview = ref(null)

    // グループの設定を使っている店舗が「個別に設定する」を押してから保存するまでの間
    const isCustomizing = ref(false)
    const editable = computed(() => !props.grouped || props.customized || isCustomizing.value)

    const normalized = computed(() => ({
      enabled: !!draft.value.enabled,
      roundingUnitMinutes: Number(draft.value.roundingUnitMinutes),
      roundingMethod: draft.value.roundingMethod,
      addMinutes: Number(draft.value.addMinutes) || 0,
      earliestTime: draft.value.earliestTime || null,
    }))

    const roundingUnits = computed(() => {
      const units = [5, 10, 15, 30]
      const current = Number(draft.value.roundingUnitMinutes)
      if (current && !units.includes(current)) units.push(current)

      return units.sort((a, b) => a - b).map(unit => ({ text: `${unit}分`, value: unit }))
    })

    // 計算例は必ずサーバーで計算する(画面に計算式を持たせない)
    const fetchPreview = debounce(async () => {
      if (!normalized.value.enabled) return

      try {
        const res = await CheckInSettingsApi.preview({
          rule: normalized.value,
          items: props.items.map(item => ({ ...item, key: String(item.id) })),
        })
        preview.value = res?.data?.preview || null
      } catch (error) {
        preview.value = null
      }
    }, 300)

    const reset = () => {
      draft.value = { ...DEFAULT_RULE, ...(props.rule || {}) }
      isCustomizing.value = false
    }

    watch(() => props.rule, reset, { immediate: true, deep: true })
    watch([normalized, () => props.items], fetchPreview, { immediate: true, deep: true })

    const inherit = () => emit('inherit')
    const cancelCustomizing = () => reset()

    return {
      draft,
      preview,
      isCustomizing,
      editable,
      normalized,
      roundingUnits,
      roundingMethods: ROUNDING_METHODS,

      inherit,
      cancelCustomizing,
    }
  },
}
</script>

<style scoped>
.check-in-preview--changed {
  color: #6b35d9;
  font-weight: 600;
}
</style>
